import { createEntityAdapter, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { type DeepPartial } from "react-hook-form";
import { all } from "redux-saga/effects";

import { deploymentsActions } from "@ds/modules/deployments/redux/slice";
import { DetailsTableDataTypeEnum, MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { getIds } from "@ds/utils/entities";
import { LoadStatus, type BaseMeta, type PatchedModel } from "@ds/utils/reducer";
import { type SagaOptions } from "@ds/utils/saga-helpers";

import { contentActions } from "../../redux/slice";
import { isCampaign } from "../utils/model";
import { watchCampaigns } from "./sagas";

export type DuplicateCampaignPayload = { campaignId: number; newTitle?: string };

export enum CampaignsRemoteOperation {
  CRUD = "crud",
  CHECKBOXES = "checkboxes",
  IOT_FETCH = "iot_fetch",
}

type CampaignsMeta = BaseMeta & {
  remoteOperation: CampaignsRemoteOperation;
  filters?: KeysToCamelCase<ContentCampaignFilters>;
};

interface CampaignsState {
  loadStatusMap: {
    [CampaignsRemoteOperation.CRUD]: LoadStatus;
    [CampaignsRemoteOperation.CHECKBOXES]: LoadStatus;
    [CampaignsRemoteOperation.IOT_FETCH]: LoadStatus;
  };

  error: string | null;

  entities: {
    [campaignId: number]: Campaign;
  };

  ids: number[];
  tableIds: Record<string, number[]>;
  selectedIds: Record<string, number[]>;
}

const entityAdapter = createEntityAdapter<Campaign>();

export const initialState: CampaignsState = entityAdapter.getInitialState({
  loadStatusMap: {
    [CampaignsRemoteOperation.CRUD]: LoadStatus.Idle,
    [CampaignsRemoteOperation.CHECKBOXES]: LoadStatus.Idle,
    [CampaignsRemoteOperation.IOT_FETCH]: LoadStatus.Idle,
  },

  error: null,

  tableIds: { [MainTableDataTypeEnum.ContentCampaigns]: [] },
  selectedIds: { [MainTableDataTypeEnum.ContentCampaigns]: [] },
}) as CampaignsState;

const getTableTypeOrDefault = (
  tableType: MainTableDataTypeEnum | DetailsTableDataTypeEnum = MainTableDataTypeEnum.ContentCampaigns,
) => tableType;

const removeEntities = (state: CampaignsState, payload: number[]) => {
  entityAdapter.removeMany(state, payload);

  state.tableIds[MainTableDataTypeEnum.ContentCampaigns] = state.tableIds[
    MainTableDataTypeEnum.ContentCampaigns
  ].filter(id => !payload.includes(id));

  state.selectedIds[MainTableDataTypeEnum.ContentCampaigns] = state.selectedIds[
    MainTableDataTypeEnum.ContentCampaigns
  ].filter(id => !payload.includes(id));
};

const sliceType = "CONTENT_CAMPAIGNS";
const slice = createSlice({
  name: sliceType,
  initialState,
  reducers: {
    fetchCampaigns: {
      reducer(state, { meta }: PayloadAction<undefined, string, CampaignsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;

        if (meta.remoteOperation === CampaignsRemoteOperation.CHECKBOXES) {
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] =
            state.tableIds[getTableTypeOrDefault(meta.options?.tableType)];
        }
      },
      prepare(
        filters: KeysToCamelCase<ContentCampaignFilters> = {},
        options: SagaOptions = {},
        remoteOperation: CampaignsRemoteOperation = CampaignsRemoteOperation.CRUD,
      ) {
        return { payload: undefined, meta: { remoteOperation, filters, options } };
      },
    },
    fetchCampaignsFailed: {
      reducer(state, { meta, error }: PayloadAction<undefined, string, CampaignsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;

        if (meta.remoteOperation === CampaignsRemoteOperation.CHECKBOXES) {
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = [];
        }
      },
      prepare(meta: CampaignsMeta, { message }: ErrorLike) {
        return { payload: undefined, meta, error: message };
      },
    },
    fetchCampaignsSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<QueryOutput<Campaign>, string, CampaignsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        entityAdapter.setMany(state, payload.items);

        if (meta.remoteOperation === CampaignsRemoteOperation.CHECKBOXES) {
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = payload.items.map(({ id }) => id);
        } else if (meta.options?.tableType) {
          state.tableIds[getTableTypeOrDefault(meta.options?.tableType)] = payload.items.map(({ id }) => id);
          state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = state.selectedIds[
            getTableTypeOrDefault(meta.options?.tableType)
          ].filter(id => state.ids.includes(id));
        }
      },
      prepare(payload: QueryOutput<Campaign>, meta: CampaignsMeta) {
        return { payload, meta };
      },
    },
    fetchCampaign: {
      reducer(state, { meta }: PayloadAction<number, string, CampaignsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(payload: number, options: SagaOptions = {}) {
        return { payload, meta: { remoteOperation: CampaignsRemoteOperation.CRUD, options } };
      },
    },
    fetchCampaignFailed: {
      reducer(state, { meta, error }: PayloadAction<number, string, CampaignsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(payload: number, meta: CampaignsMeta, { message }: ErrorLike) {
        return { payload, meta, error: message };
      },
    },
    fetchCampaignSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<Campaign, string, CampaignsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        const existing = state.entities[payload.id];
        entityAdapter.setOne(state, {
          ...payload,
          campaign_items_count: payload.campaign_items_count ?? existing?.campaign_items_count,
        });
      },
      prepare(payload: Campaign, meta: CampaignsMeta) {
        return { payload, meta };
      },
    },
    createCampaign: {
      reducer(state, { meta }: PayloadAction<Campaign, string, CampaignsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(payload: Campaign, meta: CampaignsMeta = { remoteOperation: CampaignsRemoteOperation.CRUD }) {
        return { payload, meta };
      },
    },
    createCampaignFailed: {
      reducer(state, { meta, error }: PayloadAction<Campaign, string, CampaignsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(payload: Campaign, meta: CampaignsMeta, { message }: ErrorLike) {
        return { payload, meta, error: message };
      },
    },
    createCampaignSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<Campaign, string, CampaignsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        entityAdapter.setOne(state, payload);
      },
      prepare(payload: Campaign, meta: CampaignsMeta) {
        return { payload, meta };
      },
    },
    duplicateCampaign: {
      reducer(state, { meta }: PayloadAction<DuplicateCampaignPayload, string, CampaignsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(
        payload: DuplicateCampaignPayload,
        meta: CampaignsMeta = { remoteOperation: CampaignsRemoteOperation.CRUD },
      ) {
        return { payload, meta };
      },
    },
    duplicateCampaignFailed: {
      reducer(state, { meta, error }: PayloadAction<DuplicateCampaignPayload, string, CampaignsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(payload: DuplicateCampaignPayload, meta: CampaignsMeta, { message }: ErrorLike) {
        return { payload, meta, error: message };
      },
    },
    duplicateCampaignSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<Campaign, string, CampaignsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        entityAdapter.setOne(state, payload);
      },
      prepare(payload: Campaign, meta: CampaignsMeta) {
        return { payload, meta };
      },
    },
    updateCampaigns: {
      reducer(state, _action: PayloadAction<PatchedModel<Campaign>>) {
        state.loadStatusMap[CampaignsRemoteOperation.CRUD] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(changedIds: number[], changedFields: DeepPartial<Campaign>) {
        return { payload: { ids: changedIds, data: changedFields } };
      },
    },
    updateCampaignsFailed: {
      reducer(state, { error }: PayloadAction<PatchedModel<Campaign>, string, never, string>) {
        state.loadStatusMap[CampaignsRemoteOperation.CRUD] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(payload: PatchedModel<Campaign>, { message }: ErrorLike) {
        return { payload, error: message };
      },
    },
    updateCampaignsSucceeded: (state, { payload }: PayloadAction<Campaign[]>) => {
      state.loadStatusMap[CampaignsRemoteOperation.CRUD] = LoadStatus.Succeeded;
      state.error = null;

      for (const campaign of payload) {
        const existing = state.entities[campaign.id];
        entityAdapter.setOne(state, {
          ...campaign,
          campaign_items_count: campaign.campaign_items_count ?? existing?.campaign_items_count,
        });
      }
    },
    deleteCampaigns: {
      reducer(state, { meta }: PayloadAction<number[], string, CampaignsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(idsToDelete: number[], redirectTo?: string) {
        return {
          payload: idsToDelete,
          meta: { remoteOperation: CampaignsRemoteOperation.CRUD, options: { redirectTo } },
        };
      },
    },
    deleteCampaignsFailed: {
      reducer(state, { meta, error }: PayloadAction<number[], string, CampaignsMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(idsToDelete: number[], meta: CampaignsMeta, { message }: ErrorLike) {
        return { payload: idsToDelete, meta, error: message };
      },
    },
    deleteCampaignsSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<number[], string, CampaignsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        removeEntities(state, payload);
      },
      prepare(payload: number[], meta: CampaignsMeta) {
        return { payload, meta };
      },
    },
    selectCampaigns: {
      reducer(state, { payload, meta }: PayloadAction<number[], string, CampaignsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;

        state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = payload;
      },
      prepare(campaignsOrIds: Campaign[] | number[], options: SagaOptions = {}) {
        return {
          payload: getIds(campaignsOrIds),
          meta: { remoteOperation: CampaignsRemoteOperation.CHECKBOXES, options },
        };
      },
    },
    toggleSelected: {
      reducer(state, { payload, meta }: PayloadAction<number, string, CampaignsMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;

        state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)] = state.selectedIds[
          getTableTypeOrDefault(meta.options?.tableType)
        ].includes(payload)
          ? state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)].filter(id => id !== payload)
          : [...state.selectedIds[getTableTypeOrDefault(meta.options?.tableType)], payload];
      },
      prepare(campaignId: number, options: SagaOptions = {}) {
        return {
          payload: campaignId,
          meta: { remoteOperation: CampaignsRemoteOperation.CHECKBOXES, options },
        };
      },
    },
    updateCampaignItemsCount: (state, { payload }: PayloadAction<{ campaignId: number; count: number }>) => {
      const existing = state.entities[payload.campaignId];
      if (existing) {
        entityAdapter.setOne(state, {
          ...existing,
          campaign_items_count: payload.count,
        });
      }
    },
    iotUpdate: (state, { payload }: PayloadAction<Campaign>) => {
      if (payload.audit?.deleted_at) {
        removeEntities(state, [payload.id]);
      } else {
        const existing = state.entities[payload.id];
        entityAdapter.setOne(state, {
          ...payload,
          campaign_items_count: payload.campaign_items_count ?? existing?.campaign_items_count,
        });
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(contentActions.switchView, state => {
      state.loadStatusMap[CampaignsRemoteOperation.CRUD] = LoadStatus.Idle;
      state.error = null;
    });

    builder.addCase(deploymentsActions.fetchDeploymentsSucceeded, (state, { payload, meta }) => {
      if (!meta.options?.cache?.fetchedFromCache) {
        entityAdapter.setMany(
          state,
          payload.items.flatMap(({ details }) => details?.entities || []).filter(isCampaign) as unknown as Campaign[],
        );
      }
    });

    builder.addCase(deploymentsActions.fetchDeploymentSucceeded, (state, { payload, meta }) => {
      if (!meta.options?.cache?.fetchedFromCache) {
        entityAdapter.setMany(state, (payload.details?.entities || []).filter(isCampaign) as unknown as Campaign[]);
      }
    });

    builder.addCase(deploymentsActions.createDeploymentSucceeded, (state, { payload }) => {
      entityAdapter.setMany(state, (payload.details?.entities || []).filter(isCampaign) as unknown as Campaign[]);
    });

    builder.addCase(deploymentsActions.updateDeploymentsSucceeded, (state, { payload }) => {
      entityAdapter.setMany(
        state,
        payload.flatMap(({ details }) => details?.entities || []).filter(isCampaign) as unknown as Campaign[],
      );
    });
  },
});

export const { name: campaignsType, actions: campaignsActions, reducer: campaignsReducer } = slice;

export const entitySelectors = entityAdapter.getSelectors();

export function* rootCampaignsSaga() {
  yield all([watchCampaigns()]);
}
